/**
 * How nexseed shows a date to a person: `DD/MM/YYYY`, and `DD/MM/YYYY hh:mm AM/PM` with a time.
 *
 * Display only. Everything that travels — API fields, URL params, query keys, DB values — stays
 * ISO `YYYY-MM-DD`; `parseDate` turns what an operator typed back into that.
 *
 * Three kinds of input, told apart by shape:
 * - a calendar date (`2026-09-18`) is re-ordered as text, so no timezone can move its day;
 * - a wall-clock time with no offset (`2026-09-18T10:00:00`, how drying times are stored) is the
 *   plant's own clock and is shown as written;
 * - an instant (`…Z`, `…+05:30`, a `Date`) is shown on the Indian clock. IST is a fixed +05:30
 *   with no daylight saving, so the shift is plain arithmetic — the handheld's Hermes engine is
 *   not relied on for `Intl` timezone support.
 */

type DateLike = string | Date | null | undefined;

interface Parts {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
}

const IST_OFFSET_MS = 330 * 60_000;
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const WALL_CLOCK =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

/**
 * An instant with an offset, ISO (`2026-09-17T10:23:45.123Z`) or as Postgres prints `timestamptz::text`
 * (`2026-09-17 10:23:45.123456+00`): space instead of `T`, a short offset, microseconds.
 */
const INSTANT =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(:\d{2})?(\.\d+)?(Z|[+-]\d{2}(?::?\d{2})?)$/;

const pad = (n: number) => String(n).padStart(2, "0");

/** Epoch ms for an `INSTANT` match, rebuilt as strict ISO so every engine parses it alike. */
function instantMs(m: RegExpExecArray): number {
  const [, date, hm, sec, frac, off] = m as unknown as [
    string,
    string,
    string,
    string | undefined,
    string | undefined,
    string,
  ];
  const ms = frac ? `.${frac.slice(1, 4).padEnd(3, "0")}` : "";
  const zone =
    off === "Z"
      ? "Z"
      : `${off.slice(0, 3)}:${off.slice(3).replace(":", "").padEnd(2, "0")}`;
  return Date.parse(`${date}T${hm}${sec ?? ":00"}${ms}${zone}`);
}

function istParts(ms: number): Parts {
  const t = new Date(ms + IST_OFFSET_MS);
  return {
    y: t.getUTCFullYear(),
    mo: t.getUTCMonth() + 1,
    d: t.getUTCDate(),
    h: t.getUTCHours(),
    mi: t.getUTCMinutes(),
  };
}

/** Null when `v` is not a date at all; `hasTime` is false for a bare calendar date. */
function partsOf(v: string | Date): { p: Parts; hasTime: boolean } | null {
  if (v instanceof Date) {
    const ms = v.getTime();
    return Number.isNaN(ms) ? null : { p: istParts(ms), hasTime: true };
  }
  const s = v.trim();
  const date = DATE_ONLY.exec(s);
  if (date) {
    const p = { y: +date[1]!, mo: +date[2]!, d: +date[3]!, h: 0, mi: 0 };
    return isRealDay(p.y, p.mo, p.d) ? { p, hasTime: false } : null;
  }
  const wall = WALL_CLOCK.exec(s);
  if (wall) {
    const p = {
      y: +wall[1]!,
      mo: +wall[2]!,
      d: +wall[3]!,
      h: +wall[4]!,
      mi: +wall[5]!,
    };
    return isRealDay(p.y, p.mo, p.d) && p.h < 24 && p.mi < 60
      ? { p, hasTime: true }
      : null;
  }
  // Only recognised shapes: `Date.parse` on free text differs between engines.
  const instant = INSTANT.exec(s);
  if (!instant) return null;
  const ms = instantMs(instant);
  return Number.isNaN(ms) ? null : { p: istParts(ms), hasTime: true };
}

const dmy = (p: Parts) => `${pad(p.d)}/${pad(p.mo)}/${p.y}`;

function hm(p: Parts): string {
  const h12 = p.h % 12 === 0 ? 12 : p.h % 12;
  return `${pad(h12)}:${pad(p.mi)} ${p.h < 12 ? "AM" : "PM"}`;
}

/** An empty string — or a `Date` that is not one — shows as the placeholder, like null. */
const isBlank = (v: string | Date): boolean =>
  v === "" || (v instanceof Date && Number.isNaN(v.getTime()));

/**
 * `2026-09-18` → `18/09/2026`. Blank shows `empty`; text that is not a real date (`2026-13-45`)
 * shows as given rather than dressed up as one.
 */
export function formatDate(v: DateLike, empty = "—"): string {
  if (v === null || v === undefined || isBlank(v)) return empty;
  const r = partsOf(v);
  return r ? dmy(r.p) : String(v);
}

/** `2026-09-18T08:18:00Z` → `18/09/2026 01:48 PM`. A bare date shows without a time. */
export function formatDateTime(v: DateLike, empty = "—"): string {
  if (v === null || v === undefined || isBlank(v)) return empty;
  const r = partsOf(v);
  if (!r) return String(v);
  return r.hasTime ? `${dmy(r.p)} ${hm(r.p)}` : dmy(r.p);
}

/** `2026-09-18T08:18:00Z` → `01:48 PM`. */
export function formatTime(v: DateLike, empty = "—"): string {
  if (v === null || v === undefined || isBlank(v)) return empty;
  const r = partsOf(v);
  if (!r) return String(v);
  return r.hasTime ? hm(r.p) : empty;
}

/** True when y-m-d names a day that exists (no 31-02). */
export function isRealDay(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1) return false;
  return d <= new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Years an operator could mean. `0202` is a typo for 2026, not a day in the reign of Caracalla. */
const MIN_YEAR = 1900;
const MAX_YEAR = 2999;

/**
 * What an operator typed → ISO `YYYY-MM-DD`, or null when it is not a real day.
 * Takes `DD/MM/YYYY`, with `-` or `.` also accepted between the parts.
 */
export function parseDate(text: string | null | undefined): string | null {
  const m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec((text ?? "").trim());
  if (!m) return null;
  const d = +m[1]!;
  const mo = +m[2]!;
  const y = +m[3]!;
  if (y < MIN_YEAR || y > MAX_YEAR) return null;
  return isRealDay(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null;
}

/** ISO `YYYY-MM-DD` → `DD/MM/YYYY` text for an input box; `""` when blank or not a date. */
export function isoToDmy(iso: string | null | undefined): string {
  const m = DATE_ONLY.exec((iso ?? "").trim().slice(0, 10));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/**
 * What was typed or pasted into a date box → the text to show.
 *
 * - Bare digits get their slashes as they arrive: `180920` → `18/09/20`.
 * - A pasted ISO date or timestamp, or a pasted `d/m/yyyy` or `d-m-yyyy`, is turned round whole.
 * - The operator's own text is left exactly as typed while it still fits DD/MM/YYYY (a `-` or `.`
 *   they type becomes `/`), so a digit deleted or overtyped mid-date is retyped in place instead of
 *   being re-padded into another day (`8/09/2026` must not become the 8th while they are replacing
 *   the 1 of the 18th).
 * - A separator typed straight after a one-digit day or month closes it: `1/` reads as `01/`. Only
 *   when typed: `prev` is the text before this keystroke, and a deletion that leaves `18/0/` is not
 *   padded back into `18/00/`.
 */
export function maskDateInput(raw: string, prev = ""): string {
  const t = raw.trim();
  const iso = /^(\d{4}-\d{2}-\d{2})(?:[T ].*)?$/.exec(t);
  if (iso) return isoToDmy(iso[1]);
  // Pasted, not typed one key at a time: a keystroke changes the text by one character.
  if (raw.length - prev.length > 1 && /[-/.]/.test(t)) {
    const parsed = parseDate(t);
    if (parsed) return isoToDmy(parsed);
  }
  const parts = t.split(/[-/.]/).map((x) => x.replace(/\D/g, ""));
  const fits =
    parts.length > 1 &&
    parts.length <= 3 &&
    parts[0]!.length <= 2 &&
    parts[1]!.length <= 2 &&
    (parts[2] ?? "").length <= 4;
  if (fits) {
    const last = parts.length - 1;
    if (
      parts[last] === "" &&
      parts[last - 1]!.length === 1 &&
      raw.length > prev.length
    )
      parts[last - 1] = `0${parts[last - 1]!}`;
    return parts.join("/");
  }
  const d = parts.join("").slice(0, 8);
  if (d.length > 4) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
  if (d.length > 2) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return d;
}

/** A date box holding a whole `DD/MM/YYYY` — the only text a date box commits while typing. */
export const isWholeDateText = (text: string): boolean =>
  /^\d{2}\/\d{2}\/\d{4}$/.test(text);

/**
 * `yyyy-MM-ddTHH:mm` typed on the Indian clock → the instant, ISO in UTC. For fields stored as an
 * instant (access windows, suspensions), so a PC set to another timezone still means India time.
 */
export function istWallToIso(wall: string | null | undefined): string | null {
  const m = WALL_CLOCK.exec((wall ?? "").trim());
  if (!m) return null;
  const ms =
    Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!) - IST_OFFSET_MS;
  return new Date(ms).toISOString();
}

/** An instant → `yyyy-MM-ddTHH:mm` on the Indian clock, for a date-time box. `""` when blank. */
export function isoToIstWall(v: string | Date | null | undefined): string {
  if (v === null || v === undefined || v === "") return "";
  const r = partsOf(v);
  if (!r || !r.hasTime) return "";
  const { y, mo, d, h, mi } = r.p;
  return `${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}`;
}

/** True for a bare ISO calendar date, `2026-09-18` — the shape a DATE column arrives in. */
export const isIsoDate = (s: string): boolean => DATE_ONLY.test(s);

/**
 * A typed date box checked the way the handheld's create screens need it: blank, not a real day,
 * or later than today in India each get an error in the operator's own words; otherwise the ISO day.
 */
export function checkDate(
  /** How the error names the field: "Dispatch date". */
  label: string,
  text: string,
  /** Shown when the box is empty. */
  blank: string,
  now: Date = new Date()
): { iso: string } | { error: string } {
  if (text.trim() === "") return { error: blank };
  const iso = parseDate(text);
  if (!iso) return { error: `${label} must be a date like 16/09/2026` };
  if (iso > todayIst(now)) return { error: `${label} cannot be in the future` };
  return { iso };
}

/** Today on the Indian calendar, as ISO `YYYY-MM-DD`. */
export function todayIst(now: Date = new Date()): string {
  const p = istParts(now.getTime());
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}`;
}
